/**
 * KAN-147: monthly artist statements computed from frame usage (features/artist-statement.md).
 * Pure functions: the page and the tests call exactly these. Usage comes from every device's
 * `devices/{id}/reports/{yyyy-MM}/frames/{frameId}.printCount` (soft-deleted devices and stores included: user decision,
 * artist statements only); ownership from `frame_artworks` (KAN-149). One printCount increment is one photo/cut.
 */
import { ArtistLite, FrameOwnership } from './frame-ownership.util';

export const STATEMENT_COMPANY_NAME = 'KCE Group';

export interface DeviceMonthUsage {
  deviceId: string;
  deviceName: string;
  isDeleted: boolean;
  isKonobox: boolean;
  /** `reports/{yyyy-MM}.TotalSessions` (0 when the month document is missing). */
  totalSessions: number;
  /**
   * Sessions in the month that were not bypassed (Coupon, Card and Cash count). TotalSessions also counts Bypass/AdminBypass
   * sessions, so this is read from the raw session logs, only for devices that would otherwise be flagged as missing data.
   */
  nonBypassSessions?: number;
  /** themeId / frameGroupId are written by the kiosk on each counter; they locate the frame image like the Frame Report. */
  frames: { frameId: string; printCount: number; themeId?: string; frameGroupId?: string }[];
}

/** Where a frame was printed (first device seen), used to resolve its image the way the Frame Report does. */
export interface FrameUsageSource {
  deviceId: string;
  themeId: string;
  frameGroupId: string;
}

export interface StatementArtist extends ArtistLite {
  email?: string;
  commission_amount?: unknown;
}

export interface StatementLine {
  frameId: string;
  prints: number;
  rate: number | null;
  /** Line amount rounded half-up to cents; null when the artist's rate is missing. */
  amount: number | null;
  fpImagePath: string | null;
  source?: FrameUsageSource;
}

export interface ArtistMonthStatement {
  artistId: string;
  artistName: string;
  email: string;
  inactive: boolean;
  rate: number | null;
  rateMissing: boolean;
  lines: StatementLine[];
  totalPrints: number;
  totalAmount: number | null;
}

export interface UnpaidFrame {
  frameId: string;
  prints: number;
  fpImagePath: string | null;
  ownerId?: string;
  ownerName?: string;
  source?: FrameUsageSource;
}

export interface MissingDataDevice {
  deviceId: string;
  deviceName: string;
  sessions: number;
}

export interface MonthlyStatements {
  artists: ArtistMonthStatement[];
  unassigned: UnpaidFrame[];
  needsReview: UnpaidFrame[];
  missingData: MissingDataDevice[];
  kpis: { totalFrames: number; totalPrints: number; totalAmount: number };
}

/** Half-up to cents for non-negative amounts (0.125 -> 0.13, 0.375 -> 0.38), immune to binary noise such as 1.005. */
export function roundHalfUp(value: number): number {
  return Math.round(Number((value * 100).toFixed(6))) / 100;
}

/** A usable commission: a finite number above zero. Null, 0, negative and non-numbers are "Rate missing". */
export function validRate(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : null;
}

/**
 * Builds the month: prints per frame ID summed over all devices, attributed to the frame's current owner.
 * No owner (or no ownership record) = company-owned, never paid. An owner that is missing or soft-deleted = "Needs review
 * (owner deleted)", never paid. Artists are ranked by amount (desc), then name; rate-missing artists rank last.
 * Totals count assigned usage only: Total Prints includes rate-missing artists, Total Amount does not.
 */
export function buildMonthlyStatements(
  usage: DeviceMonthUsage[],
  ownerships: Map<string, FrameOwnership>,
  artistsById: Map<string, StatementArtist>,
  fpImagePaths: Map<string, string | null> = new Map()
): MonthlyStatements {
  const printsByFrame = new Map<string, number>();
  const sources = new Map<string, FrameUsageSource>();
  for (const device of usage) {
    for (const f of device.frames) {
      const prints = Number(f.printCount);
      if (!f.frameId || !Number.isFinite(prints) || prints <= 0) continue;
      printsByFrame.set(f.frameId, (printsByFrame.get(f.frameId) || 0) + prints);
      if (!sources.has(f.frameId) && f.themeId && f.frameGroupId) {
        sources.set(f.frameId, { deviceId: device.deviceId, themeId: f.themeId, frameGroupId: f.frameGroupId });
      }
    }
  }

  const byArtist = new Map<string, ArtistMonthStatement>();
  const unassigned: UnpaidFrame[] = [];
  const needsReview: UnpaidFrame[] = [];
  for (const [frameId, prints] of printsByFrame) {
    const record = ownerships.get(frameId);
    const ownerId = record && record.isDeleted !== true ? record.artistId || null : null;
    const fpImagePath = record?.fpImagePath || fpImagePaths.get(frameId) || null;
    const source = sources.get(frameId);
    if (!ownerId) {
      unassigned.push({ frameId, prints, fpImagePath, source });
      continue;
    }
    const artist = artistsById.get(ownerId);
    if (!artist || artist.isDeleted === true) {
      needsReview.push({ frameId, prints, fpImagePath, ownerId, ownerName: artist?.name || ownerId, source });
      continue;
    }
    let stmt = byArtist.get(ownerId);
    if (!stmt) {
      const rate = validRate(artist.commission_amount);
      stmt = {
        artistId: ownerId,
        artistName: artist.name || ownerId,
        email: artist.email || '',
        inactive: artist.isActive === false,
        rate,
        rateMissing: rate === null,
        lines: [],
        totalPrints: 0,
        totalAmount: null
      };
      byArtist.set(ownerId, stmt);
    }
    stmt.lines.push({ frameId, prints, rate: stmt.rate, amount: stmt.rate === null ? null : roundHalfUp(prints * stmt.rate), fpImagePath, source });
  }

  const artists = [...byArtist.values()].map(s => {
    s.lines.sort((a, b) => b.prints - a.prints || a.frameId.localeCompare(b.frameId));
    s.totalPrints = s.lines.reduce((n, l) => n + l.prints, 0);
    s.totalAmount = s.rateMissing ? null : roundHalfUp(s.lines.reduce((n, l) => n + (l.amount || 0), 0));
    return s;
  }).filter(s => s.totalPrints > 0);

  artists.sort((a, b) => {
    if (a.rateMissing !== b.rateMissing) return a.rateMissing ? 1 : -1;
    return (b.totalAmount || 0) - (a.totalAmount || 0) || a.artistName.localeCompare(b.artistName);
  });

  const byPrintsThenId = (a: UnpaidFrame, b: UnpaidFrame) => b.prints - a.prints || a.frameId.localeCompare(b.frameId);
  unassigned.sort(byPrintsThenId);
  needsReview.sort(byPrintsThenId);

  return {
    artists,
    unassigned,
    needsReview,
    missingData: findMissingData(usage),
    kpis: {
      totalFrames: artists.reduce((n, s) => n + s.lines.length, 0),
      totalPrints: artists.reduce((n, s) => n + s.totalPrints, 0),
      totalAmount: roundHalfUp(artists.reduce((n, s) => n + (s.totalAmount || 0), 0))
    }
  };
}

/** A device that needs its raw session logs read: sessions recorded but no frame counters (Konobox devices never have frames). */
export function hasSessionsWithoutFrames(d: DeviceMonthUsage): boolean {
  return !d.isKonobox && d.totalSessions > 0 && d.frames.length === 0;
}

/** Kiosk Bypass / AdminBypass sessions (test or free runs); older logs without PaymentMethod use payType FREE/BYPASS or isSkip. */
export function isBypassSession(s: any): boolean {
  const method = String(s?.PaymentMethod || '');
  if (method) return method === 'Bypass' || method === 'AdminBypass';
  const legacy = String(s?.payType || s?.payMethod || '').toUpperCase();
  return legacy === 'FREE' || legacy === 'BYPASS' || s?.isSkip === true;
}

/**
 * A PhotoLavie device that recorded non-bypassed sessions in the month but has no frame counters. Bypassed sessions are
 * left out (2026-11-02): a machine used only for test runs that never printed must not raise the warning.
 */
export function findMissingData(usage: DeviceMonthUsage[]): MissingDataDevice[] {
  return usage
    .filter(hasSessionsWithoutFrames)
    .map(d => ({ deviceId: d.deviceId, deviceName: d.deviceName, sessions: d.nonBypassSessions ?? d.totalSessions }))
    .filter(d => d.sessions > 0)
    .sort((a, b) => a.deviceName.localeCompare(b.deviceName));
}

/** `2026/09` (statement period) -> `2026-09` (report document id). */
export function toReportPeriod(period: string): string {
  return period.replace('/', '-');
}

/** Periods 2024/01 .. the current month, latest first. */
export function statementPeriods(now: Date): string[] {
  const out: string[] = [];
  for (let d = new Date(2024, 0, 1); d <= now; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
    out.push(`${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out.reverse();
}

/** The previous full month, the default period. */
export function previousFullMonth(now: Date): string {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** `AS-{YYYYMM}-{first 4 characters of the artist ID, uppercase}-r{revision}`, e.g. AS-202609-K7QX-r1. */
export function statementNumber(period: string, artistId: string, revision: number): string {
  return `AS-${period.replace('/', '')}-${artistId.slice(0, 4).toUpperCase()}-r${revision}`;
}

/** The values an issued snapshot froze; equal signatures mean "Already issued" (no new revision). */
export function statementSignature(s: Pick<ArtistMonthStatement, 'rate' | 'lines' | 'totalPrints' | 'totalAmount'>): string {
  return JSON.stringify({
    rate: s.rate,
    lines: s.lines.map(l => [l.frameId, l.prints, l.amount]),
    totalPrints: s.totalPrints,
    totalAmount: s.totalAmount
  });
}
